// MYS-7340: the dashboard edits the call to action buttons as inline entries (like the product infos),
// so the field framework (Field / TranslateableField / translation status) can resolve the localized
// `label` through the `CtaButton` field model. The API stores them as plain CtaButton objects.
import { CtaActionType, isValidCtaValue, MAX_CTA_BUTTONS } from '../../../shared/cta-button'
import type { CtaAsset, CtaButton } from '../../../shared/cta-button'

export { CtaActionType, MAX_CTA_BUTTONS }

export type CtaEntry = {
	sys: { id: string, contentType: { sys: { id: 'CtaButton' } } }
	fields: {
		label: Record<string, string>
		actionType: CtaActionType | null
		value: Record<string, string>
		assets: Record<string, CtaAsset>
	}
}

export type CtaEntryError = 'label' | 'actionType' | 'value' | 'file'

export function newCtaEntry(): CtaEntry {
	return {
		sys: { id: 'id_' + Math.random().toString(36).substring(2, 12), contentType: { sys: { id: 'CtaButton' } } },
		fields: { label: {}, actionType: null, value: {}, assets: {} },
	}
}

export function toCtaEntries(buttons: CtaButton[] | undefined): CtaEntry[] {
	return (buttons ?? []).map(button => {
		const entry = newCtaEntry()
		entry.sys.id = button.id
		entry.fields.label = { ...(button.label ?? {}) }
		entry.fields.actionType = button.actionType ?? null
		entry.fields.value = { ...(button.value ?? {}) }
		entry.fields.assets = { ...(button.assets ?? {}) }
		return entry
	})
}

export function fromCtaEntries(entries: CtaEntry[] | undefined): CtaButton[] {
	return (entries ?? []).map(entry => {
		const button: CtaButton = {
			id: entry.sys.id,
			label: entry.fields.label,
			// incomplete buttons are rejected by the validation before saving
			actionType: entry.fields.actionType as CtaActionType,
		}
		if (entry.fields.actionType === CtaActionType.DOWNLOAD) {
			button.assets = entry.fields.assets
		}
		else if (entry.fields.actionType) {
			button.value = entry.fields.value
		}
		return button
	})
}

export function ctaEntryErrors(entry: CtaEntry, defaultLocaleCode: string): CtaEntryError[] {
	const errors: CtaEntryError[] = []
	const { label, actionType, value, assets } = entry.fields

	if (!label?.[defaultLocaleCode]?.trim()) errors.push('label')
	if (!actionType) {
		errors.push('actionType')
		return errors
	}
	if (actionType === CtaActionType.DOWNLOAD) {
		if (!assets?.[defaultLocaleCode]?.id) errors.push('file')
		return errors
	}
	// every language may have its own link / number / address, empty ones fall back to the default language
	const hasDefault = !!value?.[defaultLocaleCode]?.trim()
	const values = Object.values(value ?? {}).filter((v): v is string => !!v?.trim())
	if (!hasDefault || !values.every(v => isValidCtaValue(actionType, v.trim()))) errors.push('value')
	return errors
}

/** true when the value of this language is missing (default language only) or has an invalid format */
export function ctaValueInvalidForLocale(entry: CtaEntry, locale: string, defaultLocaleCode: string): boolean {
	const { actionType, value } = entry.fields
	if (!actionType || actionType === CtaActionType.DOWNLOAD) return false
	const localized = value?.[locale]?.trim()
	if (!localized) return locale === defaultLocaleCode
	return !isValidCtaValue(actionType, localized)
}

export function formatFileSize(bytes: number | undefined): string {
	if (!bytes && bytes !== 0) return ''
	if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
